import { useState, type FormEvent, type ReactNode } from 'react'
import { Building2, Save, X } from 'lucide-react'
import type { Plan } from '../../hooks/use-plans'

const COMMON_FEATURES = [
  'Todos os recursos clínicos',
  'Relatos via WhatsApp',
  'Análise com IA',
  'Agenda e evolução',
  'Painel profissional',
]

interface CreatePlanData {
  name: string
  slug?: string
  description?: string
  price_monthly_cents: number
  price_yearly_cents?: number
  currency?: string
  billing_model?: 'fixed' | 'custom'
  account_scope?: string
  max_units?: number | null
  max_professionals: number
  max_patients: number
  max_reports_per_month?: number | null
  max_storage_mb?: number | null
  features?: string[]
  trial_days?: number
  is_public?: boolean
  is_highlighted?: boolean
  badge_text?: string | null
  display_order?: number
}

interface UpdatePlanData {
  name?: string
  description?: string
  price_monthly_cents?: number
  price_yearly_cents?: number
  billing_model?: 'fixed' | 'custom'
  account_scope?: string
  max_units?: number | null
  max_professionals?: number
  max_patients?: number
  max_reports_per_month?: number | null
  max_storage_mb?: number | null
  features?: string[]
  trial_days?: number
  is_public?: boolean
  is_highlighted?: boolean
  badge_text?: string | null
  display_order?: number
}

interface PlanFormBaseProps {
  onCancel: () => void
  isLoading?: boolean
}

interface CreatePlanFormProps extends PlanFormBaseProps {
  mode: 'create'
  plan?: never
  onSubmit: (data: CreatePlanData) => void
}

interface EditPlanFormProps extends PlanFormBaseProps {
  mode: 'edit'
  plan: Plan
  onSubmit: (data: UpdatePlanData) => void
}

type PlanFormProps = CreatePlanFormProps | EditPlanFormProps

function toReais(cents: number | null | undefined) {
  return Math.round((cents || 0) / 100)
}

function toCents(value: number) {
  return Math.max(0, Math.round(value * 100))
}

export function PlanForm({ plan, onSubmit, onCancel, isLoading = false, mode }: PlanFormProps) {
  const [formData, setFormData] = useState({
    name: plan?.name || '',
    slug: plan?.slug || '',
    description: plan?.description || '',
    monthlyPrice: toReais(plan?.price_monthly_cents),
    billing_model: plan?.billing_model || 'fixed',
    account_scope: plan?.account_scope || 'Conta clínica',
    max_units: plan?.max_units ?? null,
    max_professionals: plan?.max_professionals || 3,
    max_patients: plan?.max_patients || 80,
    display_order: plan?.display_order || 0,
    is_highlighted: plan?.is_highlighted || false,
    badge_text: plan?.badge_text || '',
    is_public: plan?.is_public ?? true,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const isCustomBilling = formData.billing_model === 'custom'

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const validate = () => {
    const nextErrors: Record<string, string> = {}

    if (!formData.name.trim()) nextErrors.name = 'Informe o nome do plano'
    if (!formData.account_scope.trim()) nextErrors.account_scope = 'Informe o tipo de conta'
    if (!isCustomBilling && formData.monthlyPrice < 1) {
      nextErrors.monthlyPrice = 'Informe o preço mensal'
    }
    if (formData.max_units !== null && formData.max_units < 1) {
      nextErrors.max_units = 'Use pelo menos 1 unidade'
    }
    if (formData.max_professionals < 1) {
      nextErrors.max_professionals = 'Use pelo menos 1 profissional'
    }
    if (formData.max_patients < 1) {
      nextErrors.max_patients = 'Use pelo menos 1 paciente'
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!validate()) return

    const monthlyCents = isCustomBilling ? 0 : toCents(formData.monthlyPrice)
    const yearlyCents = isCustomBilling ? 0 : monthlyCents * 10
    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      price_monthly_cents: monthlyCents,
      price_yearly_cents: yearlyCents,
      currency: 'BRL',
      billing_model: formData.billing_model as 'fixed' | 'custom',
      account_scope: formData.account_scope.trim(),
      max_units: formData.max_units,
      max_professionals: formData.max_professionals,
      max_patients: formData.max_patients,
      max_reports_per_month: null,
      max_storage_mb: null,
      features: COMMON_FEATURES,
      display_order: formData.display_order,
      is_highlighted: formData.is_highlighted,
      badge_text: formData.badge_text.trim() || null,
      is_public: formData.is_public,
    }

    if (mode === 'create') {
      onSubmit({
        ...payload,
        slug: formData.slug.trim() || undefined,
      })
      return
    }

    onSubmit(payload)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                {mode === 'create' ? 'Novo plano' : `Editar ${plan.name}`}
              </h2>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Todos os planos têm os mesmos recursos. Edite apenas preço e limites comerciais.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[78vh] overflow-y-auto px-6 py-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Nome" error={errors.name}>
              <input
                type="text"
                value={formData.name}
                onChange={(event) => handleChange('name', event.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                placeholder="Básico"
              />
            </Field>

            {mode === 'create' ? (
              <Field label="Slug">
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(event) =>
                    handleChange(
                      'slug',
                      event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                    )
                  }
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  placeholder="basico"
                />
              </Field>
            ) : (
              <Field label="Identificador">
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                  {plan.slug}
                </div>
              </Field>
            )}
          </div>

          <Field label="Descrição" className="mt-4">
            <textarea
              value={formData.description}
              onChange={(event) => handleChange('description', event.target.value)}
              rows={2}
              className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
              placeholder="Resumo interno do posicionamento do plano"
            />
          </Field>

          <div className="mt-5 grid gap-4 md:grid-cols-3">
            <Field label="Modelo">
              <select
                value={formData.billing_model}
                onChange={(event) => handleChange('billing_model', event.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
              >
                <option value="fixed">Preço fixo</option>
                <option value="custom">Sob consulta</option>
              </select>
            </Field>

            <Field label="Mensalidade" error={errors.monthlyPrice}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                  R$
                </span>
                <input
                  type="number"
                  value={formData.monthlyPrice}
                  disabled={isCustomBilling}
                  onChange={(event) => handleChange('monthlyPrice', Number(event.target.value) || 0)}
                  className="w-full rounded-lg border border-gray-200 py-2 pl-10 pr-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 disabled:bg-gray-50 disabled:text-gray-400"
                  min={0}
                />
              </div>
            </Field>

            <Field label="Ordem">
              <input
                type="number"
                value={formData.display_order}
                onChange={(event) => handleChange('display_order', Number(event.target.value) || 0)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                min={0}
              />
            </Field>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-4">
            <Field label="Conta" error={errors.account_scope} className="md:col-span-1">
              <input
                type="text"
                value={formData.account_scope}
                onChange={(event) => handleChange('account_scope', event.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                placeholder="Conta clínica"
              />
            </Field>

            <Field label="Unidades" error={errors.max_units}>
              <input
                type="number"
                value={formData.max_units ?? ''}
                onChange={(event) =>
                  handleChange('max_units', event.target.value ? Number(event.target.value) : null)
                }
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                min={1}
                placeholder="Ilimitado"
              />
            </Field>

            <Field label="Profissionais" error={errors.max_professionals}>
              <input
                type="number"
                value={formData.max_professionals}
                onChange={(event) =>
                  handleChange('max_professionals', Number(event.target.value) || 1)
                }
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                min={1}
              />
            </Field>

            <Field label="Pacientes" error={errors.max_patients}>
              <input
                type="number"
                value={formData.max_patients}
                onChange={(event) => handleChange('max_patients', Number(event.target.value) || 1)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                min={1}
              />
            </Field>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-3">
            <Field label="Badge">
              <input
                type="text"
                value={formData.badge_text}
                onChange={(event) => handleChange('badge_text', event.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                placeholder="Principal"
              />
            </Field>

            <label className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2">
              <input
                type="checkbox"
                checked={formData.is_highlighted}
                onChange={(event) => handleChange('is_highlighted', event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">Destacar no grid</span>
            </label>

            <label className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2">
              <input
                type="checkbox"
                checked={formData.is_public}
                onChange={(event) => handleChange('is_public', event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">Plano público</span>
            </label>
          </div>

          <div className="mt-6 flex items-center justify-end gap-3 border-t border-gray-100 pt-5">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {isLoading ? 'Salvando...' : 'Salvar plano'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Field({
  label,
  error,
  className,
  children,
}: {
  label: string
  error?: string
  className?: string
  children: ReactNode
}) {
  return (
    <label className={className}>
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </label>
  )
}
